import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Table,
  message,
  Typography,
  Row,
  Col,
  Input,
  Select,
  Checkbox,
  Space,
  Avatar,
  Radio,
} from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBusinessContext } from '../../components/BusinessContext';
import { DeleteOutlined, DownloadOutlined, EyeOutlined, MailOutlined, CreditCardOutlined, WhatsAppOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;

const OrderSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { businessId, businessName, logo } = useBusinessContext();
  const {
    cart = [],
    tax = 0,
    discount = 0,
    totalAmount = 0,
    paymentMethod: initialPaymentMethod = 'Cash',
  } = location.state || {};

  const [invoiceSubject, setInvoiceSubject] = useState('Service per June 2023');
  const [currency, setCurrency] = useState('GH₵ - Ghana Cedis');
  const [items, setItems] = useState(cart);
  const [applyDiscount, setApplyDiscount] = useState(true);
  const [discountValue, setDiscountValue] = useState('Summer Sale 10th');
  const [paymentMethod, setPaymentMethod] = useState(initialPaymentMethod);
  const [cashReceived, setCashReceived] = useState('');
  const [change, setChange] = useState(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const keyboardRef = useRef(null); // Ref to track clicks within the keyboard
  const inputRef = useRef(null); // Ref to access the DOM element of the Input

  const currentDate = moment();
  const subtotal = items.reduce((total, item) => total + item.price * item.quantity, 0);
  const discountAmount = applyDiscount ? 0 : 0;
  const taxAmount = tax;
  const amountDue = (subtotal + taxAmount - discountAmount).toFixed(2);

  useEffect(() => {
    const cash = parseFloat(cashReceived) || 0;
    if (paymentMethod === 'Cash' && cash >= amountDue) {
      setChange((cash - amountDue).toFixed(2));
    } else {
      setChange(0);
    }
  }, [cashReceived, amountDue, paymentMethod]);

  // Auto-focus the input field when payment method is Cash
  useEffect(() => {
    if (paymentMethod === 'Cash' && inputRef.current) {
      setIsKeyboardVisible(true); // Ensure keyboard is visible
      inputRef.current.focus(); // Focus the input field
    } else {
      setIsKeyboardVisible(false); // Hide keyboard for other payment methods
    }
  }, [paymentMethod]);

  // Handle clicks outside the keyboard to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inputElement = inputRef.current?.input; // Access the underlying <input> DOM element
      const keyboardElement = keyboardRef.current;

      // Check if the click is outside both the input and the keyboard
      if (
        keyboardElement &&
        !keyboardElement.contains(event.target) &&
        inputElement &&
        !inputElement.contains(event.target)
      ) {
        setIsKeyboardVisible(true); // Close the keyboard when clicking outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const columns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          {record.images?.length > 0 ? (
            <Avatar src={record.images[0].filePath} size={40} />
          ) : (
            <Avatar size={40}>No Image</Avatar>
          )}
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Tax',
      key: 'tax',
      render: () => (
        <Select defaultValue="10%" style={{ width: 100 }}>
          <Option value="10%">10%</Option>
          <Option value="5%">5%</Option>
          <Option value="0%">0%</Option>
        </Select>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (text, record) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => setItems(items.filter(item => item.id !== record.id))}
        />
      ),
    },
  ];

  const handleProcessing = () => {
    if (!paymentMethod) {
      message.error('Please select a payment method');
      return;
    }

    const cash = parseFloat(cashReceived) || 0;
    if (paymentMethod === 'Cash') {
      if (cash < amountDue) {
        message.error('Insufficient cash received');
        return;
      }
      message.success(`Payment successful! Change: ${change} GH₵`);
    } else {
      message.success('Payment processed successfully!');
    }

    setTimeout(() => {
      navigate('/success');
    }, 2000);
  };

  // Numeric keyboard input handler
  const handleKeyPress = (value) => {
    if (value === 'clear') {
      setCashReceived('');
    } else if (value === 'backspace') {
      setCashReceived(cashReceived.slice(0, -1));
    } else if (value === '.') {
      if (!cashReceived.includes('.')) {
        setCashReceived(cashReceived + value);
      }
    } else if (typeof value === 'number') {
      // For quick amount buttons (10, 20, 50)
      setCashReceived(value.toString());
    } else {
      // For digit buttons (0-9)
      setCashReceived(cashReceived + value);
    }
  };

  // Numeric Keyboard Component
  const NumericKeyboard = () => (
    <div ref={keyboardRef}>
      <Card style={{ padding: 10, marginTop: 10 }}>
        <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
          <Col span={8}><Text strong>Balance Due: {amountDue} GH₵</Text></Col>
          <Col span={8}><Text strong>Amount Tendered: {(parseFloat(cashReceived) || 0).toLocaleString()} GH₵</Text></Col>
          <Col span={8}><Text strong>Change: {change.toLocaleString()} GH₵</Text></Col>
        </Row>
        <Row gutter={[8, 8]}>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 60,
                fontSize: 24, // Increased font size
                fontWeight: 'bold', // Bold text
              }}
              onClick={() => handleKeyPress('7')}
            >
              7
            </Button>
          </Col>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 60,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('8')}
            >
              8
            </Button>
          </Col>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 60,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('9')}
            >
              9
            </Button>
          </Col>
          <Col span={6}>
            <Button
              type="primary"
              style={{
                width: '100%',
                height: 60,
                fontSize: 18, // Slightly smaller font for quick amounts
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress(10)}
            >
              10 GH₵
            </Button>
          </Col>
        </Row>
        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 60,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('4')}
            >
              4
            </Button>
          </Col>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 60,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('5')}
            >
              5
            </Button>
          </Col>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 60,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('6')}
            >
              6
            </Button>
          </Col>
          <Col span={6}>
            <Button
              type="primary"
              style={{
                width: '100%',
                height: 60,
                fontSize: 18,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress(20)}
            >
              20 GH₵
            </Button>
          </Col>
        </Row>
        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 70,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('1')}
            >
              1
            </Button>
          </Col>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 70,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('2')}
            >
              2
            </Button>
          </Col>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 70,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('3')}
            >
              3
            </Button>
          </Col>
          <Col span={6}>
            <Button
              type="primary"
              style={{
                width: '100%',
                height: 70,
                fontSize: 18,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress(50)}
            >
              50 GH₵
            </Button>
          </Col>
        </Row>
        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 70,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('clear')}
            >
              C
            </Button>
          </Col>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 70,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('0')}
            >
              0
            </Button>
          </Col>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 70,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('.')}
            >
              .
            </Button>
          </Col>
          <Col span={6}>
            <Button
              style={{
                width: '100%',
                height: 70,
                fontSize: 24,
                fontWeight: 'bold',
              }}
              onClick={() => handleKeyPress('backspace')}
            >
              <ArrowLeftOutlined />
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  );

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        background: "#f5f5f5",
        margin: 0,
        padding: "20px",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Row gutter={[16, 16]} style={{ height: "100%" }}>
        <Col xs={26} md={14} style={{ height: "100%", overflowY: "auto" }}>
          <Title level={3}>Order Summary</Title>

          {/* Coupon and Discount */}
          <Checkbox checked={false} style={{ marginBottom: 8 }}>
            Add Coupon
          </Checkbox>
          <Checkbox
            checked={applyDiscount}
            onChange={(e) => setApplyDiscount(e.target.checked)}
            style={{ marginBottom: 16 }}
          >
            Add Discount
          </Checkbox>
          {applyDiscount && (
            <Select
              value={discountValue}
              onChange={(value) => setDiscountValue(value)}
              style={{ width: '100%', marginBottom: 16 }}
            >
              <Option value="Summer Sale 10th">Summer Sale 10th</Option>
              <Option value="Winter Sale 15th">Winter Sale 15th</Option>
            </Select>
          )}

          {/* Payment Method */}
          <Title level={5}>Payment Method</Title>
          <Radio.Group
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            style={{ marginBottom: 16, width: '100%' }}
          >
            <Radio.Button value="Cash" style={{ width: '33.33%', textAlign: 'center' }}>
              Cash
            </Radio.Button>
            <Radio.Button value="Debit Card" style={{ width: '33.33%', textAlign: 'center' }}>
              Debit Card
            </Radio.Button>
            <Radio.Button value="QRIS" style={{ width: '33.33%', textAlign: 'center' }}>
              Credit
            </Radio.Button>
          </Radio.Group>

          {/* Cash Payment Section with Numeric Keyboard */}
          {paymentMethod === 'Cash' && (
            <Card title="Cash Payment" style={{ marginBottom: 16 }}>
              <Row gutter={14}>
                <Col span={24}>
                  <Input
                    ref={inputRef}
                    type="text"
                    placeholder="Enter amount received"
                    value={cashReceived}
                    onFocus={() => setIsKeyboardVisible(true)}
                    readOnly
                    style={{ marginBottom: 10 }}
                  />
                </Col>
              </Row>
            </Card>
          )}
          {isKeyboardVisible && <NumericKeyboard />}

          {/* Footer Buttons */}
          <Row justify="space-between">
            <Col>
              <Text type="secondary"></Text>
            </Col>
            <Col>
              <Space>
                <Button onClick={() => navigate(-1)}>Cancel</Button>
                <Button type="primary" onClick={handleProcessing}>
                  Process
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>

        <Col xs={18} md={10} style={{ height: "100%", overflowY: "auto" }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Space>
              <Button icon={<EyeOutlined />}>Preview</Button>
              <Button icon={<DownloadOutlined />}>PDF</Button>
              <Button icon={<WhatsAppOutlined />}>Whatsapp</Button>
              <Button icon={<MailOutlined />}>Email</Button>
              <Button icon={<CreditCardOutlined />}>Payment page</Button>
            </Space>
          </div>
          <Title level={5}>Product</Title>
          <Table
            dataSource={items}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="small"
            style={{ marginBottom: 16 }}
          />
          <Card style={{ borderRadius: 8, boxShadow: "0 2px 5px rgba(0,0,0,0.05)" }}>
            <Title level={4}>INV2398-08-087</Title>
            <Row gutter={[14, 8]} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Text strong>Date</Text>
                <br />
                <Text>{currentDate.format('D MMMM YYYY')}</Text>
              </Col>
              <Col span={8}>
                <Text strong>Subject</Text>
                <br />
                <Text>{invoiceSubject}</Text>
              </Col>
              <Col span={8}>
                <Text strong>Currency</Text>
                <br />
                <Text>{currency}</Text>
              </Col>
              <Col span={24}>
                <Text strong>Billed to</Text>
                <br />
                <Text>Walk In Customer</Text>
              </Col>
            </Row>

            {/* Invoice Items Table */}
            <Table
              dataSource={items}
              columns={[
                { title: 'DESCRIPTION', dataIndex: 'name', key: 'name' },
                { title: 'QTY', dataIndex: 'quantity', key: 'quantity' },
                { title: 'UNIT PRICE', dataIndex: 'price', key: 'price', render: (price) => `${price.toLocaleString()} GH₵` },
                { title: 'AMOUNT', key: 'amount', render: (text, record) => `${(record.price * record.quantity).toLocaleString()} GH₵` },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
              style={{ marginBottom: 16 }}
            />

            {/* Summary */}
            <div style={{ textAlign: 'right' }}>
              <Row justify="end">
                <Col span={12}><Text>Subtotal</Text></Col>
                <Col span={12}><Text>{subtotal.toLocaleString()} GH₵</Text></Col>
              </Row>
              <Row justify="end">
                <Col span={12}><Text>Discount %</Text></Col>
                <Col span={12}><Text>{discountAmount.toLocaleString()} GH₵</Text></Col>
              </Row>
              <Row justify="end" style={{ fontWeight: 'bold', marginTop: 8 }}>
                <Col span={12}><Text>Amount due</Text></Col>
                <Col span={12}><Text>{amountDue.toLocaleString()} GH₵</Text></Col>
              </Row>
            </div>

            {/* Attachment */}
            <div style={{ marginTop: 16 }}>
              <Text strong>Attachment</Text>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <Space>
                  <DownloadOutlined />
                  <Text>Product_list.PDF (512kb)</Text>
                </Space>
                <Button type="link" icon={<DownloadOutlined />}>Download</Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OrderSummary;